import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { catchRenderException, Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-provider-components';
import { FetchAPI, FieldAPI, FieldListAPI, FieldListValue, FormAPI, InputAPI, LegendsAPI, PanelAPI, ToastNotificationAPI, UnboxedAutocompleteAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnClick, OnClose, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ReactComponent as MagnifierIcon } from '../../../resources/assets/search.svg';
import { AccreditationClinicalStaffAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import './clinical-staff.css';
import './accreditation.css';
import { AccreditationSteps } from './accreditation-update-controller';
import { ActionTypes, getStampUpdateStatus } from './update-accreditation-type';

interface ClinicalStaff {
  nrSequence: number,
  ieMainSpecialty: string | boolean,
  checkboxIeMainSpecialty: string | boolean,
  fieldOfWork: number,
  nrFieldOfWork: number,
  dsFieldOfWork: string,
  specialty: number,
  cdSpecialty: number,
  name: string,
  cdCode: string,
  ieAction: ActionTypes,
  dsAction: string,
}

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
  DETAIL: 'detail',
};

@Controller({ fragment: FragmentNames.ACCREDITATION_CLINICAL_STAFF })
export default class ClinicalStaffController extends BaseController {
  @Inject(Aliases.PANEL_MODAL)
  private panelModal: PanelAPI;
  @Inject(Aliases.FETCH_FIELD_OF_WORK)
  private fetchFieldOfWork: FetchAPI;
  @Inject(Aliases.FIELD_FIELD_OF_WORK)
  private fieldOfWork: FieldAPI;
  @Inject(Aliases.UNBOXED_FIELD_OF_WORK)
  private unboxedFieldOfWork: UnboxedAutocompleteAPI;
  @Inject(Aliases.FORM_SPECIALTY)
  private formSpecialty: FormAPI;
  @Inject(Aliases.FORM_DOCTOR)
  private formDoctor: FormAPI;
  @Inject(Aliases.HPMS_CUSTOM_LIST_FIELD)
  private specialtiesList: FieldListAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toast: ToastNotificationAPI;
  @Inject(Aliases.FIELD_TOAST)
  private fieldToast: FieldAPI;
  @Inject(Aliases.FETCH_CLINICAL_STAFF)
  private fetchClinicalStaff: FetchAPI;
  @Inject(Aliases.INPUT_FILTER)
  private inputFilter: InputAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_PENDENCIES)
  private toastPendencies: ToastNotificationAPI;
  @Inject(Aliases.PANEL_SPECIALTY_LIST)
  private panelSpecialtiesList: PanelAPI;
  @Inject(Aliases.PANEL_SPECIALTY)
  private panelSpecialty: PanelAPI;
  @Inject(Aliases.HPMS_CUSTOM_LIST_FIELD)
  private hpmsCustomListField: FieldListAPI;
  @Inject(Aliases.LEGENDS)
  private legends: LegendsAPI;

  @OnMount(Aliases.INPUT_FILTER)
  onMountFilter(): void {
    this.inputFilter.setExternalAction(<MagnifierIcon className="c-module-selector__search-icon" />);
  }

  @OnMount(Aliases.FETCH_CLINICAL_STAFF)
  onMountFetch(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    api.setParameters({ nrSeqProvider, nrSeqAccreditation });
    api.forceFetch();
  }

  @OnClick(Aliases.BUTTON_ADD_DOCTOR)
  onClickAdd(): void {
    const { nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqTypeAccreditation });
    this.panelModal.setVisible(true);
  }

  @OnChange(Aliases.INPUT_FILTER)
  onEditFilter(event: ChangeEvent<ClinicalStaff>): void {
    const params = this.fetchClinicalStaff.getParameters();
    const desc = event.value;
    this.fetchClinicalStaff.setParameters({ ...params, desc });
    this.fetchClinicalStaff.forceFetch();
  }

  @OnClose(Aliases.MODAL_ADD_NEW)
  onCloseModal(): void {
    this.panelModal.setVisible(false);
  }

  @OnChange(Aliases.FIELD_SPECIALTY)
  onChangeSpecialty(event: ChangeEvent<ClinicalStaff>): void {
    this.fieldOfWork.setValue(undefined);
    const specialtyCode = event.value;
    this.context.set({ specialtyCode });
    this.fetchFieldOfWork.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_CLINICAL_STAFF)
  onAfterFetchClinicalStaff(event: FetchEvent): void {
    const { data } = event;
    if (isEmptyArrayOrObject(data)) {
      this.legends.setShowExpression('');
      this.legends.setHideExpression('');
    } else {
      this.legends.setShowExpression(MessageService.translate('tws.legends.show'));
      this.legends.setHideExpression(MessageService.translate('tws.legends.hide'));
    }
  }

  @OnAfterFetch(Aliases.FETCH_FIELD_OF_WORK)
  OnAfterFetchFeildOfWork(event: FetchEvent): void {
    const { data } = event;
    this.unboxedFieldOfWork.setEnabled(!isEmptyArrayOrObject(data));
  }

  @OnClick(Aliases.LINK_ADD)
  onSaveActionClick(): void {
    const record = this.formSpecialty.getRecord() as unknown as ClinicalStaff;
    if (!isEmptyArrayOrObject(record) && record.cdSpecialty != undefined) {
      const value = [...this.specialtiesList.getValue()];
      const specialty = {
        cdSpecialty: record.cdSpecialty,
        nrFieldOfWork: record.nrFieldOfWork,
        dsMainSpecialty: record.checkboxIeMainSpecialty ? MessageService.translate('provider.yes-string') : MessageService.translate('provider.no-string'),
        checkboxIeMainSpecialty: record.checkboxIeMainSpecialty,
      };
      value.push(specialty);
      const reversed = [...value].reverse();
      this.specialtiesList.setValue(reversed);
      this.formSpecialty.setValue({});
    }
  }

  @OnChange(Aliases.HPMS_CUSTOM_LIST_FIELD)
  public onChangeListBeneficiarios(changeEvent: ChangeEvent<FieldListValue>): void {
    this.specialtiesList.setValue(changeEvent.value);
  }

  @OnMount(Aliases.HPMS_CUSTOM_LIST_FIELD)
  OnMountList(): void {
    this.specialtiesList.setValue([]);
  }

  @OnSubmit(Aliases.FORM_DOCTOR)
  onSubmit(): void {
    const doctor = this.formDoctor.getRecord() as unknown as ClinicalStaff;
    const specialties = this.specialtiesList.getValue();
    const body = { ...doctor, specialties };
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    this.doctorSave(Number(nrSeqProvider), Number(nrSeqAccreditation), body);
  }

  private doctorSave(nrSeqProvider: number, nrSeqAccreditation: number, body: {}): void {
    HttpService.post(Endpoint.POST.NEW_DOCTOR, { nrSeqProvider, nrSeqAccreditation }, { body })
      .then(() => {
        this.actionsPostDoctorSuccess();
        this.panelModal.setVisible(false);
        this.fetchClinicalStaff.forceFetch();
      }).catch((response) => {
        this.actionsPostDoctorFail(response);
      });
  }

  private actionsPostDoctorSuccess(): void {
    this.formSpecialty.setValue({});
    this.formDoctor.setValue({});
    this.specialtiesList.setValue([]);
    this.fieldToast.setVisible(true);
    this.toast.setLevel(ToastLevel.SUCCESS);
    this.toast.setMessage(MessageService.translate('provider.request-success'));
  }

  private actionsPostDoctorFail(response: any): void {
    catchRenderException(() => {
      this.fieldToast.setVisible(true);
      this.toast.setLevel(ToastLevel.ERROR);
      this.toast.setMessage(MessageService.translate(response.body ? response.body : 'provider.error-process-request'));
      this.scrollDownModal();
    });
  }

  scrollDownModal(): void {
    const termPanel = document.querySelector<HTMLDivElement>('.c-modal__content--with-stack-manager');
    if (termPanel) {
      const { scrollHeight } = termPanel;
      termPanel.scrollTo({ top: scrollHeight, behavior: 'smooth' });
    }
  }

  @OnClose(Aliases.TOAST_NOTIFICATION)
  onCloseToast(): void {
    this.fieldToast.setVisible(false);
  }

  @OnCancel(Aliases.FORM_DOCTOR)
  onCancelForm(): void {
    this.panelModal.setVisible(false);
  }

  @OnMount(Aliases.FORM_SPECIALTY)
  onMountFormSpecialty(event: MountEvent): void {
    const api = event.target as FormAPI;
    FormViewService.shouldEditForm(api, AccreditationSteps.CLINICAL_STAFF);
  }

  @OnMount(Aliases.FETCH_SPECIALTY)
  onMountFetchSpecialty(event: MountEvent): void {
    const { nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    const fetch = event.target as FetchAPI;
    fetch.setParameters({ nrSeqTypeAccreditation });
    fetch.forceFetch();
  }

  @OnMount(Aliases.FIELD_TOKEN_URGENCY)
  onMountTokenUrgency(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const { urgency } = api.getData();
    api.setVisible(urgency === 'S');
  }

  @OnMount(Aliases.FIELD_TOKEN_COOPERATIVE)
  onMountTokenCooperative(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const { cooperative } = api.getData();
    api.setVisible(cooperative === 'S');
  }

  @OnMount(Aliases.PANEL_ADD_BUTTOM)
  onMountAddButtom(event: MountEvent): void {
    const api = event.target as PanelAPI;
    FormViewService.shouldRenderForm(api, AccreditationSteps.CLINICAL_STAFF);
  }

  @HPMSOnCreateOption(Aliases.HPMS_CUSTOM_CARD)
  onCreateOption(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { editable } = data;
    const show = {
      [OptionKey.DETAIL]: editable == 'N',
      [OptionKey.EDIT]: editable == 'S',
      [OptionKey.DELETE]: editable == 'S',
    };
    shouldRender(show[key]);
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendendies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.CLINICAL_STAFF);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toastPendencies, event);
    }
  }

  @HPMSOnOptionClick(Aliases.HPMS_CUSTOM_CARD)
  onOptionClick(event: HpmsCustomCardEvent<ClinicalStaff>): void {
    const { data, key } = event;
    const { nrSequence } = data;
    const action = {
      [OptionKey.EDIT]: () => this.editDoctor(nrSequence, true),
      [OptionKey.DELETE]: () => this.deleteDoctor(nrSequence),
      [OptionKey.DETAIL]: () => this.editDoctor(nrSequence, false),

    };
    if (action[key]) {
      action[key]();
    }
  }

  @OnDynamicContentRender(Aliases.FILED_ACTION_STATUS)
  onRenderCardSpecialty(event: RenderEvent): void {
    const { data, render } = event;
    const { ieAction, dsAction } = data as ClinicalStaff;
    render((): ReactElement => getStampUpdateStatus(ieAction, dsAction));
  }

  private editDoctor(nrSeqDoctor: number, readOnly: boolean): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    HttpService.get<ClinicalStaff>(Endpoint.GET.DOCTOR, { nrSeqProvider, nrSeqAccreditation, nrSeqDoctor })
      .then(({ body }) => {
        this.panelModal.setVisible(true);
        this.formDoctor.setState({ editing: readOnly, disabled: !readOnly });
        if (body) {
          this.formDoctor.setRecord(body as unknown as Record<string, Value>);
          if (readOnly) {
            this.formDoctor.validate();
          }
        }
      }).catch(() => ToastService.open({ message: MessageService.translate('provider.error-process-request'), level: ToastLevel.ERROR }));

    HttpService.get<ClinicalStaff[]>(Endpoint.GET.DOCTOR_SPECIALTIES, { nrSeqProvider, nrSeqAccreditation, nrSeqDoctor })
      .then(({ body }) => {
        this.panelModal.setVisible(true);
        this.formSpecialty.setEditing(readOnly);
        if (body) {
          this.specialtiesList.setValue(body as unknown as Record<string, Value>[]);
          if (!readOnly) {
            if (body.length > 0) {
              this.panelSpecialtiesList.setVisible(false);
              this.hpmsCustomListField.setReadOnly(true);
            } else {
              this.panelSpecialty.setVisible(false);
            }
          }
        }
      }).catch(() => ToastService.open({ message: MessageService.translate('provider.error-process-request'), level: ToastLevel.ERROR }));
  }

  private deleteDoctor(nrSeqDoctor: number): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    HttpService.delete(Endpoint.DELETE.DOCTOR, { nrSeqProvider, nrSeqAccreditation, nrSeqDoctor })
      .then(() => {
        this.fetchClinicalStaff.forceFetch();
      }).catch((response) => ToastService.open(response));
  }
}
